import React, { useEffect, useRef, useState } from "react";
import { ProblemData } from "../../MathFactsGenerator/Constants.ts";
import { cardFaces } from "../cardFaces.ts";
import { AnswerResult, checkAnswer } from "./answer.ts";
import { QuizQueue } from "./quizQueue.ts";
import { useQuizClock } from "./useQuizClock.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

/** One answer a child got wrong or skipped, kept for the end-of-run summary. */
export interface Miss {
    /** The problem as it was shown, e.g. "7 x 8". */
    front: string;
    /** The correct answer, as text. */
    answer: string;
    /** What the child typed; empty string for a skip. */
    typed: string;
    skipped: boolean;
}

export interface QuizResult {
    correct: number;
    wrong: number;
    skipped: number;
    misses: Miss[];
    /** Every problem answered correctly, as shown ("7 x 8") — for the missed-facts store. */
    corrects: string[];
    /** Null for an untimed run. */
    durationMs: number | null;
}

interface QuizRunProps {
    queue: QuizQueue;
    durationMs: number | null;
    onFinish(result: QuizResult): void;
}

function formatClock(ms: number): string {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * One quiz run: a problem, a box to type the answer in, and a verdict slot that
 * never gets in the way.
 *
 * Pressing Enter marks the answer *and* moves to the next problem in the same
 * render. The verdict for the answer just given lands in a fixed-height slot
 * below the input - always present, so nothing shifts when the first one
 * appears; announced politely, so it does not interrupt typing; and safe to
 * ignore entirely for a child who wants to go fast.
 *
 * Focus never leaves the input. The input is never re-mounted between problems,
 * and nothing else is given focus, so on a phone the keyboard stays up.
 */
export function QuizRun({ queue, durationMs, onFinish }: QuizRunProps) {
    const {
        phase,
        countdownSeconds,
        remainingMs,
        start: startClock,
        stop: stopClock,
    } = useQuizClock(durationMs);
    const inputRef = useRef<HTMLInputElement>(null);
    const t = useTranslation();

    const [problem, setProblem] = useState<ProblemData>(() => queue.next());
    const [typed, setTyped] = useState("");
    const [verdict, setVerdict] = useState<{ text: string; result: AnswerResult } | null>(null);

    const [correct, setCorrect] = useState(0);
    const [wrong, setWrong] = useState(0);
    const [skipped, setSkipped] = useState(0);
    const [streak, setStreak] = useState(0);
    const misses = useRef<Miss[]>([]);
    const corrects = useRef<string[]>([]);

    // Start the clock on mount. QuizPage remounts this component (via a key) for
    // every run, so a fresh mount is a fresh run. `startClock` is stable for the
    // life of a run, so this runs once.
    useEffect(() => {
        startClock();
    }, [startClock]);

    // Hand the result up when the clock runs out (timed) or Finish is pressed.
    useEffect(() => {
        if (phase === "finished") {
            onFinish({
                correct,
                wrong,
                skipped,
                misses: misses.current,
                corrects: corrects.current,
                durationMs,
            });
        }
    }, [phase, correct, wrong, skipped, durationMs, onFinish]);

    // Keep focus on the input once answers are being taken.
    useEffect(() => {
        if (phase === "running") {
            inputRef.current?.focus();
        }
    }, [phase]);

    const accepting = phase === "running";

    /**
     * Submit is wired three ways - the form's onSubmit (the Enter button, and a
     * phone's "Go" key), and an explicit Enter handler on the input. The last is
     * not belt-and-braces: a form with one field and a submit button is meant to
     * submit on Enter on its own, but that implicit submission does not always
     * fire, and a quiz that only advances when you reach for the button is not
     * the quiz that was asked for.
     */
    function submitAnswer() {
        if (!accepting) {
            return;
        }

        const faces = cardFaces(problem);
        const result = checkAnswer(problem, typed);

        if (result === "correct") {
            setCorrect((n) => n + 1);
            setStreak((n) => n + 1);
            corrects.current.push(faces.front);
        } else {
            setStreak(0);
            if (result === "wrong") {
                setWrong((n) => n + 1);
            } else {
                setSkipped((n) => n + 1);
            }
            misses.current.push({
                front: faces.front,
                answer: faces.back,
                typed: typed.trim(),
                skipped: result === "skipped",
            });
        }

        // The verdict is for the problem just answered; the next one is already on.
        setVerdict({ text: `${faces.front} = ${faces.back}`, result });
        setProblem(queue.next());
        setTyped("");
    }

    const faces = cardFaces(problem);

    return (
        <div className="quizRun">
            <div className="quizStatus">
                {durationMs !== null && phase === "running" && (
                    <span className="quizClock" aria-label={t('flashCards.timeLeftAria')}>
                        {formatClock(remainingMs)}
                    </span>
                )}
                <span className="quizScore">{format(t('flashCards.scoreTemplate'), { n: correct })}</span>
                {streak >= 3 && <span className="quizStreak">{format(t('flashCards.streakTemplate'), { n: streak })}</span>}
            </div>

            {phase === "countdown" ? (
                <div className="quizCountdown" aria-live="assertive">
                    <p className="quizCountdownNumber">{countdownSeconds}</p>
                    <p className="quizCountdownProblem">{faces.front}</p>
                    <p className="quizCountdownHint">{t('flashCards.getReady')}</p>
                </div>
            ) : (
                <form
                    className="quizCard"
                    onSubmit={(e) => {
                        e.preventDefault();
                        submitAnswer();
                    }}
                >
                    <p className="quizProblem">{faces.front} =</p>
                    <input
                        ref={inputRef}
                        // The border carries the last verdict too, so the
                        // right/wrong signal lands for a child moving too fast
                        // to read the line below. Colour only - the box never
                        // changes size, so nothing moves.
                        className={`quizInput${verdict ? ` quizInput--${verdict.result}` : ""}`}
                        type="text"
                        inputMode="numeric"
                        autoComplete="off"
                        value={typed}
                        onChange={(e) => setTyped(e.target.value.replace(/[^\d]/g, ""))}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                submitAnswer();
                            }
                        }}
                        disabled={!accepting}
                        aria-label={format(t('flashCards.answerForAriaTemplate'), { front: faces.front })}
                    />
                    <button type="submit" className="quizSubmit" disabled={!accepting}>
                        {t('flashCards.enterButton')}
                    </button>
                </form>
            )}

            {/* Always rendered, always the same height. If it grew into being on
                the first answer, everything below - the input a child's hands
                are on - would jump. */}
            <p
                className={`quizVerdict${verdict ? ` quizVerdict--${verdict.result}` : ""}`}
                aria-live="polite"
            >
                {verdict && (
                    <>
                        <span className="quizVerdictMark" aria-hidden="true">
                            {verdict.result === "correct" ? "✓" : verdict.result === "skipped" ? "–" : "✗"}
                        </span>{" "}
                        {verdict.result === "correct"
                            ? t('flashCards.correct')
                            : verdict.result === "skipped"
                            ? t('flashCards.skipped')
                            : t('flashCards.notQuite')}
                        {" · "}
                        {verdict.text}
                    </>
                )}
            </p>

            {durationMs === null && accepting && (
                <button type="button" className="quizFinish" onClick={stopClock}>
                    {t('flashCards.finishButton')}
                </button>
            )}

            <p className="quizHint">
                {t('flashCards.quizHint')}
                {durationMs === null && t('flashCards.noClockHintSuffix')}
            </p>
        </div>
    );
}
